import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  debounceTime,
  distinctUntilChanged,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';
import { MainAPiServiceService } from '@_services/main-api-service.service';
import { FuseConfirmDialogComponent } from '../Shared/confirm-dialog/confirm-dialog.component';
import { BehaviorService } from '@_services/Behavior.service';

@Component({
  selector: 'app-help-desk',
  templateUrl: './help-desk.component.html',
  styleUrls: ['./help-desk.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HelpDeskComponent implements OnInit, OnDestroy {
  subscription: Subscription;
  subscription1: Subscription;
  sub1: Subscription;
  confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent>;
  private searchTerms = new Subject<string>();
  private destroy$ = new Subject<void>();

  /**
   * Constructor
   */
  constructor(
    public dialog: MatDialog,
    private _mainAPiServiceService: MainAPiServiceService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private behaviorService: BehaviorService,
  ) {}
  searchDataValue = '';
  searchResult = [];
  topMostResult = [];
  openWebsite: any;
  loader: any = false;
  currentUser: any = JSON.parse(localStorage.getItem('currentUser'));

  /**
   * It runs once after the component's view has been fully initialized.
   */
  ngOnInit() {
    this.sub1 = this.behaviorService.isHelpDeskOpen$.subscribe((isOpen) => {
      if (isOpen) {
        let currentRoute = this.router.url.split('/')[1];
        this.topSearches(currentRoute);
      }
    });

    this.subscription = this.searchTerms
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) => this.searchData(term)),
      )
      .subscribe((response) => {
        this.searchResult = this.plainTextResults(response.RESPONSE?.results);
      });
  }

  /**
   * Unsubscribes from help-desk streams so they do not leak after the panel is destroyed.
   */
  ngOnDestroy(): void {
    this.sub1?.unsubscribe();
    this.subscription?.unsubscribe();
    this.subscription1?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Opens the currently loaded knowledge-base article in a new browser tab.
   */
  newTab() {
    if (this.openWebsite) {
      window.open(
        this.openWebsite.changingThisBreaksApplicationSecurity,
        '_blank',
        'noopener,noreferrer',
      );
    }
  }

  /**
   * Pushes the search box value into the debounced search stream.
   * @param event Native keyup event from the search input.
   */
  onKeyUp(event: any): void {
    const searchValue = event.target.value;
    this.searchTerms.next(searchValue);
  }

  /**
   * Requests knowledge-base articles that match the given search term.
   * @param searchValue Text entered in the help desk search box.
   * @returns Observable of the knowledge-base search API response.
   */
  searchData(searchValue: any) {
    let data = {
      Action: 'search',
      SearchTerm: searchValue,
    };

    return this._mainAPiServiceService.getSetData(data, 'knowledge-base');
  }

  /**
   * Loads an article URL in the in-panel iframe, forcing HTTPS.
   * @param url Article URL from the search result.
   */
  goTo(url) {
    // this.openWebsite = encodeURI(url);
    if (url) {
      this.openWebsite = this.sanitizer.bypassSecurityTrustResourceUrl(
        url.replace('http://', 'https://'),
      );
    } else {
      this.openWebsite = undefined;
    }
    this.loader = true;
    setTimeout(() => {
      this.loader = false;
    }, 2000);
  }

  /**
   * Closes the article iframe and returns to the help desk home view.
   */
  backToHome(): void {
    this.openWebsite = undefined;
  }

  /**
   * Clears the current search so the top-searches and support lists show again.
   */
  backToHelp(): void {
    this.searchResult = [];
    this.searchDataValue = '';
  }

  /**
   * Closes the help desk side panel.
   * @param key Unused panel key kept for the template click binding.
   */
  close(key) {
    this.behaviorService.isHelpDeskOpen$.next(false);
  }

  /**
   * Returns search hits with HubSpot highlight tags stripped from title and description.
   * @param results Raw knowledge-base result rows.
   * @returns The same rows with plain-text title and description.
   */
  private plainTextResults(results: any[] = []): any[] {
    return (results || []).map((item) => ({
      ...item,
      title: this.stripHtml(item.title),
      description: this.stripHtml(item.description),
    }));
  }

  /**
   * Removes HTML tags and collapses extra whitespace so titles keep normal word spacing.
   * @param value Title or description that may contain HubSpot highlight markup.
   * @returns Plain text, or an empty string when the value is missing.
   */
  private stripHtml(value: string): string {
    return (value || '')
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Opens the contact-support dialog with phone and email details.
   */
  openDialog() {
    let confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, {
      data: {
        isContactUs: true,
        message:
          "<p>Call us on <a href='tel:1300556689'><b>1300 55 66 89</b></a>  or you can email us at <a href='mailto:support@silq.com.au'>support@silq.com.au</a>. Please provide as much detailed information as possible so we can assist you.<p>",
        // message:"<p>Please email us at <a href='mailto:support@silq.com.au'>support@silq.com.au</a>. Please provide as much detailed information as possible so we can assist you.</p>"
      },
      panelClass: 'dialog-panel-class',
      backdropClass: 'cdk-overlay-dark-backdrop',
    });
    confirmDialogRef.componentInstance.confirmMessage = '';
    confirmDialogRef.componentInstance.confirmTitle = 'Contact support';
  }

  /**
   * Loads the top knowledge-base articles for the current page/route.
   * @param searchValue First URL segment used as the search term.
   */
  topSearches(searchValue: any): void {
    let data = {
      Action: 'search',
      SearchTerm: searchValue,
      Limit: 5,
      MinScore: 0.04,
      PopularityBoost: 2.0,
    };

    this.subscription1 = this._mainAPiServiceService
      .getSetData(data, 'knowledge-base')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.topMostResult = this.plainTextResults(response.RESPONSE?.results);
        },
      });
  }

  /**
   * Opens the ScreenConnect remote-support session in a new tab.
   */
  openRemoteSupport(): void {
    window.open('https://silq.screenconnect.com/', '_blank', 'noopener,noreferrer');
  }

  /**
   * Opens the knowledge-base article that explains how to start a screen session.
   */
  needRemoteHelpSupport(): void {
    window.open(
      'https://community.silq.com.au/en/how-does-silq-login-to-my-screen-screenconnect',
      '_blank',
      'noopener,noreferrer',
    );
  }
}
